import { RenderableValue, isRenderedValue } from "./renderedValue";

type DataWithTimestamp = [Date, ...number[]];
// The first array item is the titles, and the remaining items start with the date, and then the
// values
export type GraphData = [RenderableValue[], ...DataWithTimestamp[]] | [];

export interface GraphDataPreparationOptions {
  // The target bucket range is used to figure out how many bars to downsample to.
  // Downsampling is done by following the downsample ladder, and using the target bucket range for scoring.
  // A number of buckets is considered 'ideal' if it is between this range, inclusive.
  // Outside of that, a number of buckets is scored by the proportional difference between that
  // number and the bucket count.
  //
  // For example, a bucket count of 7 that is supposed to be at minimum 10 is 30% different.
  // A bucket count of 30 that should be at maximum 25 is 20% different, so it is preferable to 7.
  targetBucketRange: { min: number; max: number; ideal: number };
  // The maximum number of categories (colors) to display on the graph. If there are more than this
  // many categories, the categories are ranked by visual weight, and those after the first
  // maxCategories - 1 are combined into an 'Others' category.
  maxCategories: number;
  // The downsample ladder is the options the graph will consider for downsampling the data, in milliseconds.
  // Downsampling to a ladder rung will only occur under the following situations:
  // 1. The time resolution of the data is a divisor of the ladder rung
  // 2. The number of buckets at that ladder rung is better than at the current resolution/rung.
  downsampleLadder: number[];
  // The axis bounds of the graph may be different from the bounds of the actual data.
  axisBounds?: { min: Date; max: Date };
}

/*
Sketch:
- ideal bar width is (axis duration) / (ideal buckets)
- when we have axis bounds, we _only_ score on how close the ladder rung duration is to the ideal
    duration
- scoring is linear interpolation between min-ideal-max, and then proportional past that
- After deciding on a ladder rung, we pad with 0 values on either side if those are not past the bounds
- For small amounts of data, we pad on either side according to the ideal duration
*/

const DefaultGraphPreparationOptions: GraphDataPreparationOptions = {
  targetBucketRange: { min: 30, max: 50, ideal: 40 },
  maxCategories: 4,
  downsampleLadder: [
    1,
    2,
    4,
    5,
    8,
    10,
    15,
    20,
    30,
    40,
    45,
    60, // 1 hour
    120, // 2 hours
    180, // 3 hours
    240, // 4 hours
    360, // 6 hours
    480, // 8 hours
    720, // 12 hours
    1440, // 1 day
    2880, // 2 days
    4320, // 3 days
    5760, // 4 days
  ].map((v) => v * 60 * 1000),
};

// Return a copy of `data` that has been preprocessed and ready to pass into an `ElvGraph`.
//
// Errors in the following cases:
// - The graphdata does not follow the type specification for `GraphData`.
// - The processing went wrong internally (should never happen)
// - The time intervals provided are not multiples of a common time 'resolution', offset by the
//     start time.
export function prepareGraph(
  data: GraphData,
  options?: Partial<GraphDataPreparationOptions>,
): GraphData {
  const p = new GraphPreparer(options);
  if (data.length === 0 || data.length === 1) {
    return [];
  } else if (data.length === 2) {
    let retData = [...data.map((v) => [...v])] as GraphData;
    p.sortColumns(retData);
    retData = p.combineExcessEntries(retData);
    return p.addEnclosingZeroes(retData, p.idealBarWidth());
  }

  p.validateGraphData(data);

  // Deep copy
  let retData = [...data.map((v) => [...v])] as GraphData;
  let maybeAddColsWithRoles = (v: GraphData) => {
    return v;
  };

  p.sortByDate(retData);
  if (data.length >= 4) {
    let resolution = p.findTimeResolution(retData);
    let finalResolution = resolution;
    if (!p.validateTimeResolution(retData, resolution)) {
      console.log(`Failed to validate with resolution ${resolution}`);
      if (p.validateTimeResolution(retData, 2 * 60 * 1000)) {
        finalResolution = 2 * 60 * 1000;
        resolution = finalResolution;
      }
    }
    [retData, maybeAddColsWithRoles] = p.normalizeToResolution(
      retData,
      resolution,
    );
    [retData, finalResolution] = p.downsample(retData, resolution);
    retData = p.addEnclosingZeroes(retData, finalResolution);
  } else {
    if (p.options.axisBounds !== undefined) {
      // Data has length exactly 3
      retData = p.handleTwoPoints(retData);
      retData = p.addEnclosingZeroes(retData, p.idealBarWidth());
    }
  }

  p.sortColumns(retData);
  // Remove columns beyond the ones that can be displayed by the graph
  retData = p.combineExcessEntries(retData);

  retData = maybeAddColsWithRoles(retData);

  return retData;
}

// GraphPreparer is a _very_ lightweight class that only exists to hold options across all these different graph modifying functions
//
// Do not call the internal methods of GraphPreparer unless you know what you are doing. The methods
// assume certain things about the data to be true, and may break in unexpected ways if called out
// of order.
export class GraphPreparer {
  options: GraphDataPreparationOptions;

  constructor(options?: Partial<GraphDataPreparationOptions>) {
    this.options = { ...DefaultGraphPreparationOptions, ...options };
    this.validateGraphOptions();
  }

  validateGraphOptions() {
    if (
      this.options.axisBounds &&
      this.options.axisBounds.min >= this.options.axisBounds.max
    ) {
      console.error(
        "Options validation error. axis bounds are likely reversed.",
        this.options.axisBounds,
      );
      const bounds = this.options.axisBounds;
      this.options.axisBounds = {
        min: bounds.max,
        max: bounds.min,
      };
    }
    const tbr = this.options.targetBucketRange;
    if (!(tbr.min <= tbr.ideal) || !(tbr.ideal <= tbr.max)) {
      console.error("Bad target bucket range", tbr);
      throw new Error("Bad target bucket range");
    }
  }

  validateGraphData(data: GraphData) {
    let nCols: number = 0;
    for (let i = 0; i < data.length; i++) {
      if (typeof data[i] !== "object") {
        throw new Error("Data validation error. Graphdata item is not array.");
      }
      if (i === 0) {
        nCols = data[i]!.length - 1;
        for (const c of data[i]!) {
          if (typeof c !== "string" && !isRenderedValue(c)) {
            throw new Error(
              "Data validation error. Graphdata header item is not string or rendered value.",
            );
          }
        }
      } else {
        if (data[i]!.length !== nCols + 1) {
          throw new Error(
            "Data validation error. Inconsistent number of columns",
          );
        }
        if (typeof data[i]![0]! !== "object") {
          throw new Error(
            "Data validation error. First element of row is not a date.",
          );
        }
        for (let j = 1; j < data[i]!.length; j++) {
          if (typeof data[i]![j] !== "number") {
            throw new Error(
              "Data validation error. Subsequent element of row is not a number.",
            );
          }
        }
      }
    }
  }

  // Sort the data in-place in ascending date order, with the title staying at the start
  sortByDate(data: GraphData) {
    data.sort((a, b) => {
      if (typeof a[0] === "string" || isRenderedValue(a[0])) {
        return -1;
      } else if (typeof b[0] === "string" || isRenderedValue(b[0])) {
        return 1;
      }

      return a[0].valueOf() - b[0].valueOf();
    });
  }

  // Find the minimum resolution of timestamps, in unix millis
  findTimeResolution(data: GraphData): number {
    let smallestResolution: number | undefined = undefined;
    // Ignore final entry, as influx sends the most recent data at that time
    for (let i = 2; i < data.length - 1; i++) {
      const millis1 = data[i - 1][0].valueOf() as number;
      const millis2 = data[i][0].valueOf() as number;
      const delta = millis2 - millis1;
      if (smallestResolution === undefined) {
        smallestResolution = delta;
      } else if (smallestResolution > delta) {
        smallestResolution = delta;
      }
    }
    if (smallestResolution === undefined) {
      throw new Error(
        "Internal graph preparation error. Undefined resolution.",
      );
    }
    return smallestResolution;
  }

  validateTimeResolution(data: GraphData, resolutionMillis: number): boolean {
    const startMillis = data[1]![0].valueOf() as number;
    let valid = true;
    for (let i = 1; i < data.length - 1; i++) {
      const measurementMillis = data[i][0].valueOf() as number;
      const isMultiple =
        (measurementMillis - startMillis) % resolutionMillis === 0;
      if (!isMultiple) {
        valid = false;
      }
    }
    return valid;
  }

  // Mutate the data by ensuring that there is a zero value for every multiple of the resolution from
  // the start value to the end value.
  normalizeToResolution(
    data: GraphData,
    resolutionMillis: number,
  ): [GraphData, (pre: GraphData) => GraphData] {
    const out: GraphData | [string[]] = [data[0]!];
    const nCols: number = data[0]!.length - 1;
    const startMillis = data[1]![0].valueOf() as number;

    let i = 1;
    let currentMillis = startMillis;
    while (i < data.length) {
      const iMillis = data[i]![0].valueOf() as number;
      while (currentMillis + resolutionMillis < iMillis) {
        out.push([
          new Date(currentMillis + resolutionMillis),
          ...new Array(nCols).fill(0),
        ]);
        currentMillis += resolutionMillis;
      }
      out.push(data[i] as any);
      currentMillis = iMillis;
      i += 1;
    }

    // Check for the leading edge of the data being uneven
    const secondLast = data.at(-2)![0].valueOf() as number;
    const last = data.at(-1)![0].valueOf() as number;
    const finalDiff = last - secondLast;

    let modifyFunction = (v: GraphData) => v;

    if (finalDiff < resolutionMillis) {
      const newLast = secondLast + resolutionMillis;
      data.at(-1)![0] = new Date(newLast);
      // Add tooltip row
      modifyFunction = (v: GraphData) => {
        // MAYBE: Make custom tooltip for _every_ row which displays new date, etc
        // This is annoying to do because you cannot just modify the date format
        v[0]!.push({ type: "string", role: "annotation" } as any);
        for (let i = 1; i < v.length - 1; i++) {
          v[i].push(undefined as any);
        }
        v.at(-1)!.push("Live" as any);
        return v;
      };
    }

    return [out as GraphData, modifyFunction];
  }

  downsample(data: GraphData, resolution: number): [GraphData, number] {
    const startRows = data.length - 1;
    const nCols: number = data[0]!.length - 1;

    let downsampleFactor = 1;
    let endRows = startRows;
    let ladderIdx = this.options.downsampleLadder.findIndex(
      (ladderMillis) => ladderMillis > resolution,
    );
    while (
      (endRows > this.options.targetBucketRange.max ||
        this.options.axisBounds !== undefined) &&
      ladderIdx < this.options.downsampleLadder.length
    ) {
      const ladderMillis = this.options.downsampleLadder[ladderIdx];
      if (ladderMillis % resolution !== 0) {
        ladderIdx += 1;
        continue;
      }
      // Compare number of rows before vs after downsampling
      const afterDownsampleFactor = ladderMillis / resolution;
      const afterEndRows = startRows / afterDownsampleFactor;

      const nowScore = this.calculateDownsampleScore(
        endRows,
        resolution * downsampleFactor,
      );
      const afterScore = this.calculateDownsampleScore(
        afterEndRows,
        resolution * afterDownsampleFactor,
      );

      if (nowScore < afterScore) {
        // It won't improve anymore by testing larger ladder capacities
        break;
      }

      downsampleFactor = afterDownsampleFactor;
      endRows = afterEndRows;
      ladderIdx += 1;
    }

    const downsampled = [];
    // Let the oldest data get an incomplete downsample if necessary
    // So iterate over the data in reverse
    for (let i = 0; i < endRows; i++) {
      // [leftIdx, rightIdx)
      const rightIdx = data.length - i * downsampleFactor;
      let leftIdx = data.length - (i + 1) * downsampleFactor;
      // Ignore header row
      if (leftIdx < 1) {
        leftIdx = 1;
      }

      const downsampledTS = data[rightIdx - 1]![0] as Date;
      const downsampledData = [
        downsampledTS,
        ...data[rightIdx - 1].slice(1),
      ] as DataWithTimestamp;
      for (let dataIdx = leftIdx; dataIdx < rightIdx; dataIdx++) {
        // Start colIdx at 1 to skip dates
        for (let colIdx = 1; colIdx < nCols + 1; colIdx++) {
          // Downsample with max, as that seems to make sense for session counts
          downsampledData[colIdx] = Math.max(
            downsampledData[colIdx] as number,
            data[dataIdx][colIdx] as number,
          );
        }
      }

      downsampled.push(downsampledData);
    }

    downsampled.reverse();

    return [[data[0]!, ...downsampled], resolution * downsampleFactor];
  }

  calculateDownsampleScore(rows: number, ladderDurationMillis: number): number {
    const bounds = this.options.axisBounds;
    if (bounds !== undefined) {
      const idealDuration = this.idealBarWidth();
      return Math.abs(idealDuration - ladderDurationMillis) / idealDuration;
    }
    const tbr = this.options.targetBucketRange;
    if (rows >= tbr.min && rows <= tbr.max) {
      return 0;
    } else if (rows > tbr.max) {
      return (rows - tbr.max) / tbr.max;
    } else {
      return (tbr.min - rows) / tbr.min;
    }
  }

  // Sort columns after the first by overall data magnitude
  sortColumns(data: GraphData) {
    const nCols = data[0]!.length - 1;
    if (nCols === 1) {
      return;
    }

    const colSums: number[] = new Array(nCols).fill(0);
    for (let i = 1; i < data.length; i++) {
      for (let j = 1; j < nCols + 1; j++) {
        colSums[j - 1] += data[i][j] as number;
      }
    }

    // Sort in descending order, with the column index at 1st index
    const colSumsSorted = colSums
      .map((v, i) => [v, i + 1])
      .sort((a, b) => b[0] - a[0]);

    for (let i = 0; i < data.length; i++) {
      const newRow = new Array(nCols + 1).fill(0);
      for (let j = 1; j < newRow.length; j++) {
        const idx = colSumsSorted[j - 1][1];
        newRow[j] = data[i][idx];
      }

      for (let j = 1; j < newRow.length; j++) {
        data[i][j] = newRow[j];
      }
    }
  }

  combineExcessEntries(data: GraphData): GraphData {
    // Filter based on total for each column, as that maps directly to total visual weight
    const nCols = data[0]!.length - 1;
    if (nCols <= this.options.maxCategories) {
      return data;
    }

    const colSums: number[] = new Array(nCols).fill(0);
    for (let i = 1; i < data.length; i++) {
      for (let j = 1; j < nCols + 1; j++) {
        colSums[j - 1] += data[i][j] as number;
      }
    }

    // Sort in descending order, with the column index at 1st index
    const colSumsSorted = colSums
      .map((v, i) => [v, i])
      .sort((a, b) => b[0] - a[0]);

    const outHeader: RenderableValue[] = [];
    outHeader.push("Time");
    for (let j = 0; j < this.options.maxCategories - 1; j++) {
      outHeader.push(data[0]![colSumsSorted[j][1] + 1]);
    }
    outHeader.push("Others");
    const out: GraphData = [outHeader];

    for (let i = 1; i < data.length; i++) {
      const rowData = data[i] as DataWithTimestamp;
      const outRow: DataWithTimestamp = [rowData[0]];
      let othersSum: number = (rowData.slice(1) as number[]).reduce(
        (tot, v) => tot + v,
        0,
      );

      for (let j = 0; j < this.options.maxCategories - 1; j++) {
        const sortedColData = rowData[colSumsSorted[j][1] + 1] as number;
        outRow.push(sortedColData);
        othersSum -= sortedColData;
      }

      outRow.push(othersSum);
      out.push(outRow);
    }

    return out;
  }

  addEnclosingZeroes(data: GraphData, finalResolution: number): GraphData {
    const bounds = this.options.axisBounds;
    if (bounds === undefined) {
      return data;
    }

    const out = [...data];
    const nCols = data[0]!.length - 1;

    const max = bounds.max.valueOf();
    const min = bounds.min.valueOf();

    const dataMin = data[1]![0].valueOf();
    const dataMax = data.at(-1)![0].valueOf() as number;
    if (dataMin - finalResolution > min) {
      const newDate = new Date(dataMin - finalResolution);
      const newRow: DataWithTimestamp = [newDate, ...new Array(nCols).fill(0)];
      out.splice(1, 0, newRow);
    }

    if (dataMax + finalResolution < max) {
      const newDate = new Date(dataMax + finalResolution);
      const newRow: DataWithTimestamp = [newDate, ...new Array(nCols).fill(0)];
      out.push(newRow);
    }

    return out as GraphData;
  }

  handleTwoPoints(data: GraphData): GraphData {
    if (data.length !== 3 || this.options.axisBounds === undefined) {
      console.error("handleTwoPoints assumptions violated");
      return data;
    }
    const t1 = data[1][0].valueOf();
    const t2 = data[2][0].valueOf();
    if (t2 - t1 < this.idealBarWidth()) {
      const newRow = data[2].map((v, i) => {
        if (i === 0) {
          return v;
        } else {
          return Math.max(v as number, data[1][i] as number);
        }
      });
      return [data[0], newRow] as any;
    } else {
      return data;
    }
  }

  idealBarWidth(): number {
    const bounds = this.options.axisBounds;
    if (bounds === undefined) {
      return 0;
    }
    const start = bounds.min.valueOf();
    const end = bounds.max.valueOf();
    return (end - start) / this.options.targetBucketRange.ideal;
  }
}
